import { create } from "zustand";
import type { ActivityActor } from "src/lib/activityService";
import { Constants } from "src/constant";
import {
  deleteHostedImage,
  listHostedImages,
  uploadHostedImage,
} from "src/lib/imageHostingService";
import type { HostedImageListItem } from "src/types/hostedImage";

export type ImageHostingStore = {
  items: HostedImageListItem[];
  loading: boolean;
  uploading: boolean;
  error: string | null;
  success: string | null;
  busyId: string | null;

  file: File | null;
  fileError: string | null;
  expiryDays: number;

  setFile: (file: File | null) => void;
  setFileError: (msg: string | null) => void;
  setExpiryDays: (days: number) => void;
  clearMessages: () => void;
  reset: () => void;

  load: (uid: string) => Promise<void>;
  upload: (
    uid: string,
    ownerDisplayName: string,
    actor: ActivityActor | null,
  ) => Promise<void>;
  remove: (
    item: HostedImageListItem,
    actor: ActivityActor | null,
  ) => Promise<void>;
};

const initialState = {
  items: [] as HostedImageListItem[],
  loading: true,
  uploading: false,
  error: null as string | null,
  success: null as string | null,
  busyId: null as string | null,
  file: null as File | null,
  fileError: null as string | null,
  expiryDays: Constants.IMAGE_HOSTING_DEFAULT_EXPIRY_DAYS,
};

export const useImageHostingStore = create<ImageHostingStore>((set, get) => ({
  ...initialState,

  setFile: (file) => set({ file, fileError: null }),
  setFileError: (msg) => set({ fileError: msg }),
  setExpiryDays: (days) => set({ expiryDays: days }),
  clearMessages: () => set({ error: null, success: null }),
  reset: () => set({ ...initialState }),

  load: async (uid) => {
    set({ loading: true, error: null });
    try {
      const items = await listHostedImages(uid);
      set({ items, loading: false });
    } catch (e: unknown) {
      set({
        error: e instanceof Error ? e.message : "Failed to load uploads",
        loading: false,
      });
    }
  },

  upload: async (uid, ownerDisplayName, actor) => {
    const { file, expiryDays } = get();
    set({ error: null, success: null });
    if (!file) {
      set({ fileError: "Pick an image first." });
      return;
    }
    set({ uploading: true });
    try {
      const saved = await uploadHostedImage(
        { file, expiryDays, ownerDisplayName },
        uid,
        actor,
      );
      set((s) => ({
        items: [saved, ...s.items],
        file: null,
        fileError: null,
        success: "Image uploaded — copy the link below to share it.",
        expiryDays: Constants.IMAGE_HOSTING_DEFAULT_EXPIRY_DAYS,
      }));
    } catch (err: unknown) {
      set({ error: err instanceof Error ? err.message : "Upload failed" });
    } finally {
      set({ uploading: false });
    }
  },

  remove: async (item, actor) => {
    if (!window.confirm(`Delete "${item.fileName}"? The link stops working.`)) {
      return;
    }
    set({ busyId: item.id, error: null, success: null });
    try {
      await deleteHostedImage(item, actor);
      set((s) => ({
        items: s.items.filter((i) => i.id !== item.id),
        success: "Image deleted.",
      }));
    } catch (err: unknown) {
      set({ error: err instanceof Error ? err.message : "Could not delete" });
    } finally {
      set({ busyId: null });
    }
  },
}));
